/**
 * Calendar time, in the office's zone, with no library.
 *
 * Jobber gives instants; the office lives in one zone (America/Chicago until
 * someone says otherwise — SchedulingTasks.md 🚩). Every "which day is this
 * on" question goes through `partsInZone`, so a 7 pm Central visit stored as
 * 00:00 UTC the next day lands on the right column. Day keys are `YYYY-MM-DD`
 * in that zone and are the unit the grid is built from.
 */

export const OFFICE_TIME_ZONE = 'America/Chicago';

/** Monday. Contractors plan Monday to Friday and the weekend goes on the end. */
export const WEEK_STARTS_ON = 1;

export type DayKey = string; // YYYY-MM-DD

export interface ZoneParts {
  readonly year: number;
  readonly month: number; // 1-12
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly weekday: number; // 0 = Sunday
}

const partFormatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = partFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short' });
    partFormatters.set(tz, f);
  }
  return f;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function partsInZone(instant: Date, tz: string = OFFICE_TIME_ZONE): ZoneParts {
  const parts: Record<string, string> = {};
  for (const p of formatter(tz).formatToParts(instant)) parts[p.type] = p.value;
  return {
    year: Number(parts['year']),
    month: Number(parts['month']),
    day: Number(parts['day']),
    hour: Number(parts['hour']) % 24,
    minute: Number(parts['minute']),
    weekday: WEEKDAYS.indexOf(parts['weekday'] ?? 'Sun'),
  };
}

export function dayKeyOf(instant: Date, tz: string = OFFICE_TIME_ZONE): DayKey {
  const p = partsInZone(instant, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/** Minutes since midnight in the zone — for placing a block on the day view. */
export function minutesOfDay(instant: Date, tz: string = OFFICE_TIME_ZONE): number {
  const p = partsInZone(instant, tz);
  return p.hour * 60 + p.minute;
}

function keyToUtcNoon(key: DayKey): Date {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** Day arithmetic on keys, immune to DST because it never leaves the calendar. */
export function addDays(key: DayKey, n: number): DayKey {
  const d = keyToUtcNoon(key);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function weekdayOf(key: DayKey): number {
  return keyToUtcNoon(key).getUTCDay();
}

/** The Monday (WEEK_STARTS_ON) on or before the key. */
export function startOfWeek(key: DayKey): DayKey {
  const back = (weekdayOf(key) - WEEK_STARTS_ON + 7) % 7;
  return addDays(key, -back);
}

export function startOfMonth(key: DayKey): DayKey {
  return `${key.slice(0, 7)}-01`;
}

export function daysInMonth(key: DayKey): number {
  const [y, m] = key.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Consecutive keys from `from`, `count` long. */
export function dayRange(from: DayKey, count: number): DayKey[] {
  return Array.from({ length: count }, (_, i) => addDays(from, i));
}

/** Every key an item touches in the zone: one for a same-day item, several for a multi-day one. */
export function daysCovered(startAt: Date, endAt: Date | null, tz: string = OFFICE_TIME_ZONE): DayKey[] {
  const first = dayKeyOf(startAt, tz);
  if (!endAt) return [first];
  // An end exactly at midnight belongs to the previous day (a 9–5 becomes a
  // 9–12:00 next-day in UTC only by conversion; a true end at 00:00 local is
  // the end of the day before).
  const endParts = partsInZone(endAt, tz);
  const endKey = endParts.hour === 0 && endParts.minute === 0 && endAt.getTime() > startAt.getTime() ? addDays(dayKeyOf(endAt, tz), -1) : dayKeyOf(endAt, tz);
  const out: DayKey[] = [];
  for (let k = first; k <= endKey && out.length < 62; k = addDays(k, 1)) out.push(k);
  return out.length > 0 ? out : [first];
}

/** The instant a day key starts in the zone — for API windows. */
export function startOfDayInstant(key: DayKey, tz: string = OFFICE_TIME_ZONE): Date {
  // Find the UTC instant whose zone parts are 00:00 on `key`: start from UTC
  // midnight and correct by the zone offset observed there.
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  const guess = new Date(Date.UTC(y, m - 1, d, 0, 0));
  const p = partsInZone(guess, tz);
  const guessKeyMinutes = (Date.UTC(p.year, p.month - 1, p.day) - Date.UTC(y, m - 1, d)) / 60000 + p.hour * 60 + p.minute;
  return new Date(guess.getTime() - guessKeyMinutes * 60000);
}

export function formatTime(instant: Date, tz: string = OFFICE_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(instant).toLowerCase().replace(' ', '');
}

export function formatDayHeading(key: DayKey): string {
  const d = keyToUtcNoon(key);
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' }).format(d);
}

/** "Sep 22" — a table cell's date, no year or weekday (the row is this year's data). */
export function formatShortDate(instant: Date, tz: string = OFFICE_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(instant);
}

/** "Sep 22" for a day that is a date, not an instant (`YYYY-MM-DD`, read at noon UTC so no zone moves it): a table cell's date. */
export function formatShortDay(key: DayKey): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(keyToUtcNoon(key));
}

/** "Sep 14, 2026" — a fact's date: no weekday, the year spelled out. */
export function formatMediumDate(instant: Date, tz: string = OFFICE_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }).format(instant);
}

/** "Tue Sep 22, 2026" — a job's start/end, where the day of week matters for scheduling. */
export function formatLongDate(instant: Date, tz: string = OFFICE_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(instant);
}

/** "Sep 23, 5:02pm" — a note or file's timestamp. */
export function formatDateTime(instant: Date, tz: string = OFFICE_TIME_ZONE): string {
  return `${formatShortDate(instant, tz)}, ${formatTime(instant, tz)}`;
}

export function formatMonthHeading(key: DayKey): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(keyToUtcNoon(key));
}

export function formatRelative(from: Date, to: Date): string {
  const s = Math.max(0, Math.round((to.getTime() - from.getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

export function todayKey(now: Date = new Date(), tz: string = OFFICE_TIME_ZONE): DayKey {
  return dayKeyOf(now, tz);
}
